/**
 * RemoveDocumentModal - confirmation dialog shown when the user removes a
 * document that previously seeded other form sections via OCR. The dialog
 * informs the user which dependent fields will be cleared; on confirm, all
 * listed fields are reset automatically.
 *
 * Ported from PartnerOS-FRONTEND. Section type is widened to `string` so the
 * component can be reused across courier onboarding sections.
 */

import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface DependentField {
  section: string;
  field: string;
  label: string;
  currentValue: string;
}

interface RemoveDocumentModalProps {
  open: boolean;
  documentLabel: string;
  dependents: DependentField[];
  onClose: () => void;
  onConfirm: (resetFields: DependentField[]) => void;
}

const RemoveDocumentModal: React.FC<RemoveDocumentModalProps> = ({
  open,
  documentLabel,
  dependents,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const resettable = useMemo(
    () => dependents.filter((d) => d.currentValue.trim().length > 0),
    [dependents],
  );

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') return null;

  const key = (d: DependentField) => `${d.section}.${d.field}`;
  const handleConfirm = () => onConfirm(resettable);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ type: 'spring', damping: 26, stiffness: 320 }}
            className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-doc-title"
          >
            <header className="flex items-start gap-3 px-5 sm:px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white">
              <span className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                <Trash2 size={20} />
              </span>
              <div className="flex-1 min-w-0">
                <h2
                  id="remove-doc-title"
                  className="text-base sm:text-lg font-semibold text-gray-900"
                >
                  {t('onboarding.documents.removeConfirm.title', {
                    defaultValue: 'Remove this document?',
                  })}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 break-words">
                  {documentLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="close"
                className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>
            </header>

            <div className="px-5 sm:px-6 py-4 overflow-y-auto flex-1 space-y-4">
              {resettable.length > 0 ? (
                <>
                  <p className="text-sm text-gray-700">
                    {t('onboarding.documents.removeConfirm.resetIntroAuto', {
                      defaultValue:
                        'The following fields were filled from this document and will be cleared:',
                    })}
                  </p>

                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                      {t('onboarding.documents.removeConfirm.resetFields', {
                        defaultValue: 'Fields to reset',
                      })}
                    </span>
                  </div>

                  <ul className="flex flex-col gap-1.5">
                    {resettable.map((d) => (
                      <li key={key(d)}>
                        <div className="flex items-start gap-2.5 p-3 rounded-xl border border-haat-red-200 bg-haat-red-50/50">
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-gray-800">
                              {d.label}
                            </span>
                            <span className="block text-xs text-gray-500 mt-0.5 truncate">
                              {d.currentValue}
                            </span>
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}

              <p className="text-xs text-gray-500 leading-relaxed">
                {t('onboarding.documents.removeConfirm.cannotBeUndone', {
                  defaultValue: 'This action cannot be undone.',
                })}
              </p>
            </div>

            <footer className="px-5 sm:px-6 py-3 border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-100 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-red-600 hover:bg-red-700 text-white shadow-sm transition-colors"
              >
                <Trash2 size={16} />
                <span>
                  {t('onboarding.documents.removeConfirm.confirm', {
                    defaultValue: 'Remove',
                  })}
                </span>
              </button>
            </footer>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default RemoveDocumentModal;
